import React from 'react';
import { X, Filter, Users, AlertCircle, CheckCircle2, Tag as TagIcon } from 'lucide-react';
import UserAvatar from './UserAvatar';
import TagChip from './TagChip';
import './BoardFilters.css';
import './Tags.css';

function BoardFilters({ filters, onFilterChange, project, onClose }) {
  // `tags` post-dates the other three (WR-010); default it so a filter state
  // restored from before this feature can't crash the panel.
  const tagFilter = filters.tags || [];
  const activeCount =
    filters.assignees.length + filters.priorities.length + filters.statuses.length + tagFilter.length;
  const hasFilters = activeCount > 0;

  const toggleArrayFilter = (filterKey, value) => {
    const current = filters[filterKey] || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onFilterChange({ ...filters, [filterKey]: updated });
  };

  const clearFilters = () => {
    onFilterChange({ assignees: [], priorities: [], statuses: [], tags: [] });
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content board-filters-modal" onClick={e => e.stopPropagation()}>
        <div className="board-filters">
      <div className="board-filters-header">
        <div className="board-filters-title">
          <Filter size={16} />
          <span>Filters</span>
          {hasFilters && <span className="filter-count">{activeCount}</span>}
        </div>
        {hasFilters && (
          <button onClick={clearFilters} className="btn btn-ghost btn-sm">
            <X size={14} />
            Clear all
          </button>
        )}
      </div>

      <div className="board-filters-content">
        {/* Assignee Filter */}
        <div className="filter-group">
          <label className="filter-label">
            <Users size={14} />
            Assignee
          </label>
          <div className="filter-options">
            {project.members?.map(member => (
              <button
                key={member._id}
                className={`filter-chip ${filters.assignees.includes(member._id) ? 'active' : ''}`}
                onClick={() => toggleArrayFilter('assignees', member._id)}
              >
                <UserAvatar user={member} className="avatar avatar-xs" style={{ width: '20px', height: '20px', fontSize: '10px' }} />
                <span>{member.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Priority Filter */}
        <div className="filter-group">
          <label className="filter-label">
            <AlertCircle size={14} />
            Priority
          </label>
          <div className="filter-options">
            {['high', 'medium', 'low'].map(priority => (
              <button
                key={priority}
                className={`filter-chip priority-${priority} ${filters.priorities.includes(priority) ? 'active' : ''}`}
                onClick={() => toggleArrayFilter('priorities', priority)}
              >
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Status Filter */}
        <div className="filter-group">
          <label className="filter-label">
            <CheckCircle2 size={14} />
            Work Status
          </label>
          <div className="filter-options">
            {[
              { value: 'todo', label: 'To Do' },
              { value: 'inprogress', label: 'In Progress' },
              { value: 'done', label: 'Done' }
            ].map(status => (
              <button
                key={status.value}
                className={`filter-chip status-${status.value} ${filters.statuses.includes(status.value) ? 'active' : ''}`}
                onClick={() => toggleArrayFilter('statuses', status.value)}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tag Filter (WR-010) — OR across the selection. */}
        {project.tags?.length > 0 && (
          <div className="filter-group">
            <label className="filter-label" title="Shows tasks carrying any of the selected tags">
              <TagIcon size={14} />
              Tags
              <span className="filter-label-note">any of</span>
            </label>
            <div className="filter-options">
              {project.tags.map(tag => (
                <button
                  key={tag._id}
                  className={`filter-chip filter-chip-tag ${tagFilter.some(id => String(id) === String(tag._id)) ? 'active' : ''}`}
                  onClick={() => toggleArrayFilter('tags', String(tag._id))}
                >
                  <TagChip tag={tag} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}

export default BoardFilters;
